import { RefObject, useRef, useState } from 'react';
import { Label, Message } from '@iwoca/orion';
import { Trans } from '@lingui/react/macro';
import { Column } from '@tanstack/react-table';
import { DateTimePicker } from 'components/DatePicker/DateTimePicker';
import { FilterActionButtons } from 'components/FilterActionButtons/FilterActionButtons';
import { TApplication } from 'lendingApi/applications.types';
import { dateToString } from 'utils/common/common';
import { useTrackFilter } from '../useTrackFilter.hook';
import styles from './DateRangeColumnFilter.module.css';

type TProps = {
  column: Column<TApplication, unknown>;
  onClose: () => void;
};

export const DateRangeColumnFilter = ({ column, onClose }: TProps) => {
  const filterValue = column.getFilterValue() as [string?, string?];
  const { trackRangeFilterApplied, trackFilterReset } = useTrackFilter(
    column.columnDef.header,
  );
  const [dateFrom, setDateFrom] = useState(filterValue?.[0] ?? '');
  const [dateTo, setDateTo] = useState(filterValue?.[1] ?? '');
  const [hasError, setHasError] = useState(false);
  const fromDateInputRef = useRef<HTMLInputElement>(null);
  const toDatePickerRef = useRef<HTMLInputElement>(null);

  const handleApply = () => {
    if (!dateFrom && !dateTo) {
      onClose();
      return;
    }
    if (new Date(dateFrom) > new Date(dateTo)) {
      setHasError(true);
      return;
    }
    column.setFilterValue([dateFrom, dateTo]);
    trackRangeFilterApplied(dateFrom, dateTo);
    onClose();
  };

  const handleReset = () => {
    setDateFrom('');
    setDateTo('');
    setHasError(false);
    if (fromDateInputRef.current && toDatePickerRef.current) {
      fromDateInputRef.current.value = '';
      toDatePickerRef.current.value = '';
    }
    column.setFilterValue(undefined);
    trackFilterReset();
  };

  const renderDatePicker = (
    defaultValue: string,
    setValue: (value: string) => void,
    dateInputRef: RefObject<HTMLInputElement>,
  ) => {
    return (
      <DateTimePicker
        withInput
        defaultDate={defaultValue}
        filterDate={() => true}
        dateInputRef={dateInputRef}
        handleChange={(date: Date | null) => {
          if (!date) return;
          setHasError(false);
          setValue(dateToString(date));
        }}
      />
    );
  };

  return (
    <div className={styles.filter}>
      <div className={styles.dateContainer}>
        <Label>
          <Trans>From</Trans>
        </Label>
        {renderDatePicker(dateFrom, setDateFrom, fromDateInputRef)}
      </div>
      <div className={styles.dateContainer}>
        <Label>
          <Trans>To</Trans>
        </Label>
        {renderDatePicker(dateTo, setDateTo, toDatePickerRef)}
      </div>
      {hasError && (
        <Message
          className={styles.error}
          message="Please select a valid date range."
          type="error"
          size="small"
        />
      )}
      <FilterActionButtons
        onResetClick={handleReset}
        onApplyClick={handleApply}
      />
    </div>
  );
};
